import { Logger } from '../logger/Logger';
import { Telemetry } from '../telemetry/Telemetry';

import { ParseXml } from './converters/parseXml';
import { ForbiddenError } from './errors/ForbiddenError';
import { NetworkError } from './errors/NetworkError';
import { ParsingError } from './errors/ParsingError';
import { ForbiddenErrors } from './errors/types';
import { UnauthorizedError } from './errors/UnauthorizedError';
import { isSessionExpired, isSessionTerminated } from './errors/utils';
import type { FetcherFetchOptions, FetcherJson } from './types';

class FetcherRoot {
  private readonly ttl: number = 30 * 60 * 1000; // 30 minutes
  private readonly cache: Record<string, { time: number; payload: Promise<unknown> }> = {};

  private static instance: FetcherRoot;

  public static getInstance(): FetcherRoot {
    if (!this.instance) {
      this.instance = new FetcherRoot();
    }
    return this.instance;
  }

  public getEndpoint(url: string): string {
    /** Remove proxy if url includes one */
    const proxy = '/proxy/';
    const index = url.indexOf(proxy);
    if (index !== -1) {
      url = url.substring(index + proxy.length);
    }
    try {
      return new URL(url).pathname;
    } catch (error) {
      // If the URL is not valid, we return the original URL, but removing the query string
      return url.split('?')[0] || '';
    }
  }

  public fetch<Payload, Body = FetcherJson>(url: string, options?: FetcherFetchOptions<Body>): Promise<Payload> {
    const originalUrl = url;

    if (options?.params) {
      const parameters = Object.entries(options.params as Record<string, string>).filter(
        ([, value]) => value !== undefined
      );
      if (parameters.length) {
        const searchParams = new URLSearchParams(parameters);
        url = `${url}?${searchParams.toString()}`;
      }
    }

    const cached = this.cache[url];
    if (!options?.force && cached && cached.time > Date.now() && options?.method !== 'POST') {
      return cached.payload as Promise<Payload>;
    }

    const { body, ...rest } = options || {};

    const shouldStringifyBody = body?.toString() == '[object Object]'; // TODO: Improve this check
    const preparedBody = shouldStringifyBody && body ? JSON.stringify(body) : (body as XMLHttpRequestBodyInit);

    const endpoint = options?.logAs ?? this.getEndpoint(url);

    if (!options?.silent) {
      Logger.logEvent('fetch', {
        additional: endpoint,
      });
    }

    this.cache[url] = {
      time: Date.now() + (options?.ttl || this.ttl),
      payload: fetch(url, { signal: options?.signal, body: preparedBody, ...rest })
        .then((response: Response) => {
          if (!response.ok) {
            if (response.status === 401) {
              throw new UnauthorizedError(`UnauthorizedError`, endpoint, originalUrl);
            }
            if (response.status === 403) {
              return response.text().then((responseText) => {
                if (isSessionTerminated(responseText)) {
                  throw new ForbiddenError(ForbiddenErrors.RESTRICT_USER, endpoint, originalUrl);
                }
                throw new ForbiddenError(`ForbiddenError`, endpoint, originalUrl);
              });
            }

            throw new NetworkError(`${response.status}`, response.body, endpoint, originalUrl);
          }
          let result: Promise<unknown>;
          switch (options?.format) {
            case 'text':
              result = response.text().catch((e) => {
                throw new ParsingError('TEXT', e.message, endpoint, originalUrl);
              });
              break;
            case 'xml':
              result = response
                .text()
                .then(ParseXml.parseString)
                .catch((e) => {
                  throw new ParsingError('XML', e.message, endpoint, originalUrl);
                });
              break;
            default:
              result = response.text().then<unknown>((text: string) => {
                /**
                 * Temporary hack until we can rely on that backend is sending json responses from json endpoints.
                 * Fallback to XML because of the inconsistency in the backend error responses.
                 */
                try {
                  return JSON.parse(text);
                } catch (error) {
                  const xmlResponse = ParseXml.parseString(text);
                  if (isSessionExpired(xmlResponse)) {
                    throw new UnauthorizedError(`UnauthorizedError`, endpoint, originalUrl);
                  }
                  return xmlResponse;
                }
              });
              break;
          }

          if (options?.root) {
            const root: string = options.root;
            result = result.then((data: any) => {
              try {
                return data[root];
              } catch (error) {
                throw new ParsingError('[Fetcher] Invalid response, unable to parse');
              }
            }) as Promise<Payload>;
          }

          return result as Promise<Payload>;
        })
        // Clean-up on Error and Re-Throw
        .catch((error: Error | NetworkError | ParsingError) => {
          const endpoint = this.getEndpoint(url);
          // For those CORS errors we default to this.
          let failurePlace = error.message;

          if (error instanceof UnauthorizedError) {
            failurePlace = `UnauthorizedError`;
            /** Invalid session error should be logged only once per loading attempt */
            Telemetry.reportFailure(
              {
                name: 'session',
                payload: [failurePlace, JSON.stringify({ url: originalUrl, endpoint: endpoint })],
              },
              {
                once: true,
              }
            );
          } else {
            if (error instanceof NetworkError) {
              failurePlace = `${error.name} | Code: ${error.message}`;
            }
            if (error instanceof ParsingError) {
              failurePlace = `${error.message} ${error.name}`;
            }
            Logger.logFailure('fetch', {
              place: failurePlace,
              additional: JSON.stringify({ url: originalUrl, endpoint: endpoint }),
            });
          }

          delete this.cache[url];
          throw error;
        }),
    };

    return this.cache[url]?.payload as Promise<Payload>;
  }
}

export const Fetcher = FetcherRoot.getInstance();
